import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, CalendarRange, Plus, Trash2, TrendingDown, ArrowLeft, List, LayoutGrid, Pencil } from "lucide-react";
import api, { fmtEur } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LeggendaFiscale } from "../components/LeggendaFiscale";
import { SOGLIA_AMM, COEFF_AMMORTAMENTO } from "../config/fiscale";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const MESI_SHORT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const num = (v) => (v === "" || v == null ? 0 : Number(String(v).replace(",", ".")) || 0);
const durataMesi = (aliquota) => (aliquota > 0 ? Math.round((100 / aliquota) * 12) : 0);
function meseIndex(dataInizio, mkey) {
  if (!dataInizio || dataInizio.length < 7) return null;
  const [sy, sm] = dataInizio.slice(0, 7).split("-").map(Number);
  const [cy, cm] = mkey.split("-").map(Number);
  return (cy - sy) * 12 + (cm - sm);
}
function calcRiga(r, mkey) {
  const idx = meseIndex(r.data_inizio, mkey);
  const dur = durataMesi(r.aliquota);
  const attivo = idx !== null && idx >= 0 && (dur === 0 || idx < dur);
  const quotaPiena = (num(r.valore) * (num(r.aliquota) / 100)) / 12;
  const quotaMensile = attivo ? quotaPiena : 0;
  const quotaDeducibile = quotaMensile * (num(r.deducibilita) / 100);
  let mesiMaturati = idx === null || idx < 0 ? 0 : idx + 1;
  if (dur > 0) mesiMaturati = Math.min(mesiMaturati, dur);
  const ammortizzato = Math.min(quotaPiena * mesiMaturati, num(r.valore));
  const residuo = Math.max(num(r.valore) - ammortizzato, 0);
  return { ...r, attivo, idx, dur, quotaMensile, quotaDeducibile, residuo, ammortizzato };
}

export default function Ammortamenti() {
  const { selectedStructure, structures = [] } = useAuth();
  const qc = useQueryClient();
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [detail, setDetail] = useState(null); // mkey "YYYY-MM" oppure null (lista)
  const [vista, setVista] = useState("tabella"); // "tabella" | "griglia"
  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const emptyForm = { descrizione: "", valore: "", aliquota: "", deducibilita: "100", data_inizio: "" };
  const [form, setForm] = useState(emptyForm);
  const realStructs = structures.filter((s) => s.id !== "all");

  const { data: cespiti = [] } = useQuery({
    queryKey: ["ammortamenti", selectedStructure],
    queryFn: async () => (await api.get("/ammortamenti-cespiti", { params: { structure_id: selectedStructure } })).data,
  });
  const { data: speseAll = [] } = useQuery({
    queryKey: ["spese-amm", selectedStructure],
    queryFn: async () => (await api.get("/spese", { params: { structure_id: selectedStructure } })).data,
  });
  // beni capitalizzati dalle Spese, mostrati (sola lettura) insieme ai cespiti
  const capRows = speseAll.filter((s) => s.capitalizza && num(s.aliquota_amm) > 0).map((s) => ({
    id: `spesa-${s.id}`, descrizione: (s.descrizione || s.fornitore || "Bene") + " (da Spese)",
    valore: num(s.totale), aliquota: num(s.aliquota_amm),
    deducibilita: (s.deducibilita_perc == null || s.deducibilita_perc === "") ? 100 : num(s.deducibilita_perc),
    data_inizio: (s.data_documento || "").slice(0, 10), _fromSpesa: true,
  }));
  const rows = [...cespiti, ...capRows];

  const mkeyOf = (m) => `${anno}-${String(m + 1).padStart(2, "0")}`;
  const totMese = (mkey) => {
    let tot = 0, ded = 0, n = 0;
    rows.forEach((r) => { const c = calcRiga(r, mkey); if (c.attivo) { tot += c.quotaMensile; ded += c.quotaDeducibile; n++; } });
    return { tot, ded, n };
  };
  const totAnno = MESI.reduce((a, _, m) => a + totMese(mkeyOf(m)).tot, 0);

  const salva = async () => {
    if (!form.descrizione.trim()) return toast.error("Inserisci la descrizione");
    if (num(form.aliquota) <= 0) return toast.error("Inserisci un'aliquota annua valida");
    if (!form.data_inizio) return toast.error("Inserisci la data di inizio");
    try {
      const payload = {
        descrizione: form.descrizione, valore: num(form.valore), aliquota: num(form.aliquota),
        deducibilita: num(form.deducibilita), data_inizio: form.data_inizio,
      };
      if (editId) {
        await api.put(`/ammortamenti-cespiti/${editId}`, payload);
      } else {
        await api.post("/ammortamenti-cespiti", { ...payload, structure_id: selectedStructure === "all" ? (realStructs[0]?.id || null) : selectedStructure });
      }
      qc.invalidateQueries({ queryKey: ["ammortamenti"] });
      setForm(emptyForm); setEditId(null); setAddOpen(false); toast.success(editId ? "Ammortamento aggiornato" : "Ammortamento aggiunto");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const openAdd = () => { setEditId(null); setForm(emptyForm); setAddOpen(true); };
  const openEdit = (r) => { setEditId(r.id); setForm({ descrizione: r.descrizione || "", valore: String(r.valore ?? ""), aliquota: String(r.aliquota ?? ""), deducibilita: String(r.deducibilita ?? "100"), data_inizio: (r.data_inizio || "").slice(0, 10) }); setAddOpen(true); };
  const del = async (id) => { try { await api.delete(`/ammortamenti-cespiti/${id}`); qc.invalidateQueries({ queryKey: ["ammortamenti"] }); toast.success("Eliminato"); } catch { toast.error("Errore"); } };

  const AddDialog = (
    <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) { setEditId(null); setForm(emptyForm); } }}>
      <DialogContent data-testid="amm-add-dialog">
        <DialogHeader><DialogTitle>{editId ? "Modifica ammortamento" : "Nuovo ammortamento"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label className="text-sm font-semibold">Descrizione</Label>
            <Input value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} placeholder="Es. Arredi camere" className="mt-1" data-testid="amm-f-descrizione" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-sm font-semibold">Valore ammortizzabile (€)</Label>
              <Input type="text" inputMode="decimal" value={form.valore} onChange={(e) => setForm({ ...form, valore: e.target.value })} placeholder="0" className="mt-1 text-right tabnum" data-testid="amm-f-valore" /></div>
            <div><Label className="text-sm font-semibold">Aliquota annua (%)</Label>
              <Input type="text" inputMode="decimal" value={form.aliquota} onChange={(e) => setForm({ ...form, aliquota: e.target.value })} placeholder="Es. 15" className="mt-1 text-right tabnum" data-testid="amm-f-aliquota" /></div>
          </div>
          <div>
            <Label className="text-sm font-semibold">Categoria bene (imposta l'aliquota)</Label>
            <Select value="" onValueChange={(v) => setForm({ ...form, aliquota: v })}>
              <SelectTrigger className="mt-1" data-testid="amm-f-categoria"><SelectValue placeholder="Scegli una categoria…" /></SelectTrigger>
              <SelectContent>{COEFF_AMMORTAMENTO.map((c) => <SelectItem key={c.cat} value={String(c.perc)}>{c.cat} · {c.perc}%</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {num(form.valore) > 0 && num(form.valore) <= SOGLIA_AMM && (
            <div className="text-xs text-amber-800 rounded-md bg-amber-50 border border-amber-200 px-3 py-2" data-testid="amm-soglia-note">
              Importo ≤ {fmtEur(SOGLIA_AMM)}: questo bene potrebbe essere <b>dedotto interamente nell'anno</b> senza ammortamento (soglia art. 102 TUIR). Valuta con il commercialista.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-sm font-semibold">Deducibilità (%)</Label>
              <Input type="text" inputMode="decimal" value={form.deducibilita} onChange={(e) => setForm({ ...form, deducibilita: e.target.value })} placeholder="100" className="mt-1 text-right tabnum" data-testid="amm-f-deducibilita" /></div>
            <div><Label className="text-sm font-semibold">Inizio ammortamento</Label>
              <Input type="date" value={form.data_inizio} onChange={(e) => setForm({ ...form, data_inizio: e.target.value })} className="mt-1" data-testid="amm-f-data" /></div>
          </div>
          {num(form.aliquota) > 0 && num(form.valore) > 0 && (
            <div className="text-xs text-muted-foreground rounded-md bg-secondary/40 px-3 py-2" data-testid="amm-preview">
              Quota annua: <b>{fmtEur(num(form.valore) * num(form.aliquota) / 100)}</b> · quota mensile: <b>{fmtEur(num(form.valore) * num(form.aliquota) / 100 / 12)}</b> · durata ~{Math.round(100 / num(form.aliquota))} anni
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setAddOpen(false)} data-testid="amm-cancel">Annulla</Button>
          <Button onClick={salva} className="font-semibold" data-testid="amm-save">{editId ? "Salva modifiche" : "Aggiungi"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // ---- DETTAGLIO MESE ----
  if (detail) {
    const righe = rows.map((r) => calcRiga(r, detail));
    const attive = righe.filter((r) => r.attivo);
    const tot = attive.reduce((a, r) => a + r.quotaMensile, 0);
    const ded = attive.reduce((a, r) => a + r.quotaDeducibile, 0);
    const [y, m] = detail.split("-").map(Number);
    return (
      <div className="space-y-5" data-testid="ammortamenti-detail">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => setDetail(null)} data-testid="amm-back"><ArrowLeft size={16} className="mr-1.5" /> Tutti i mesi</Button>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white"><CalendarRange size={18} /><span className="font-head font-bold text-lg capitalize">{MESI[m - 1]} {y}</span></div>
          </div>
          <Button onClick={openAdd} className="font-semibold" data-testid="amm-add-btn"><Plus size={16} className="mr-1.5" /> Aggiungi ammortamento</Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="gr-card p-5 border-l-4" style={{ borderColor: "#c2660c" }} data-testid="amm-tot-quota">
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Quota ammortamento del mese</div>
            <div className="text-3xl font-head font-bold text-amber-700 mt-1">{fmtEur(tot)}</div>
            <div className="text-[11px] text-muted-foreground mt-1">{attive.length} cespiti in ammortamento</div>
          </div>
          <div className="gr-card p-5 border-l-4" style={{ borderColor: "#1f7a5c" }} data-testid="amm-tot-ded">
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Di cui deducibile</div>
            <div className="text-3xl font-head font-bold text-primary mt-1">{fmtEur(ded)}</div>
            <div className="text-[11px] text-muted-foreground mt-1">Quota × % di deducibilità</div>
          </div>
        </div>
        <div className="gr-card p-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b">
                <th className="py-2 px-2">Descrizione</th><th className="py-2 px-2 text-right">Valore</th><th className="py-2 px-2 text-right">Aliquota</th>
                <th className="py-2 px-2 text-right">Deducibilità</th><th className="py-2 px-2">Inizio</th><th className="py-2 px-2 text-right">Quota mese</th><th className="py-2 px-2 text-right">Deducibile</th><th className="py-2 px-2 text-right">Residuo</th><th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Nessun ammortamento. Clicca "Aggiungi ammortamento".</td></tr>}
              {righe.map((r) => (
                <tr key={r.id} className={`border-b last:border-0 ${r.attivo ? "" : "opacity-45"}`} data-testid={`amm-row-${r.id}`}>
                  <td className="py-2 px-2 font-semibold">{r.descrizione}{!r.attivo && <span className="ml-2 text-[10px] uppercase font-bold text-muted-foreground">{r.idx === null || r.idx < 0 ? "non iniziato" : "concluso"}</span>}</td>
                  <td className="py-2 px-2 text-right tabnum">{fmtEur(r.valore)}</td>
                  <td className="py-2 px-2 text-right tabnum">{num(r.aliquota)}%</td>
                  <td className="py-2 px-2 text-right tabnum">{num(r.deducibilita)}%</td>
                  <td className="py-2 px-2 text-xs">{r.data_inizio || "—"}</td>
                  <td className="py-2 px-2 text-right tabnum font-bold text-amber-700">{fmtEur(r.quotaMensile)}</td>
                  <td className="py-2 px-2 text-right tabnum text-primary">{fmtEur(r.quotaDeducibile)}</td>
                  <td className="py-2 px-2 text-right tabnum font-semibold" data-testid={`amm-residuo-${r.id}`}>{r.residuo <= 0 ? <span className="text-muted-foreground">{fmtEur(0)}</span> : fmtEur(r.residuo)}</td>
                  <td className="py-2 px-2 text-right">{r._fromSpesa ? <span className="text-[10px] font-bold uppercase text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5" data-testid={`amm-fromspesa-${r.id}`}>da Spese</span> : <span className="inline-flex gap-0.5"><Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`amm-edit-${r.id}`}><Pencil size={15} className="text-muted-foreground" /></Button><Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => del(r.id)} data-testid={`amm-del-${r.id}`}><Trash2 size={15} className="text-muted-foreground" /></Button></span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {AddDialog}
      </div>
    );
  }

  // ---- LISTA MESI ----
  return (
    <div className="space-y-5" data-testid="ammortamenti-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><TrendingDown size={22} className="text-amber-600" /> Ammortamenti</h1>
          <p className="text-sm text-muted-foreground">Scegli un mese per vedere il dettaglio delle quote di competenza.</p>
        </div>
        <div className="flex items-center gap-2">
          <LeggendaFiscale />
          <Button onClick={openAdd} className="font-semibold" data-testid="amm-add-btn"><Plus size={16} className="mr-1.5" /> Aggiungi ammortamento</Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => setAnno((a) => a - 1)} data-testid="amm-year-prev"><ChevronLeft size={18} /></Button>
          <span className="font-head font-bold text-xl tabnum" data-testid="amm-year">{anno}</span>
          <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => setAnno((a) => a + 1)} data-testid="amm-year-next"><ChevronRight size={18} /></Button>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-muted-foreground">Totale anno: <span className="font-bold text-amber-700 tabnum">{fmtEur(totAnno)}</span></div>
          <div className="inline-flex rounded-lg border overflow-hidden">
            <button onClick={() => setVista("tabella")} data-testid="amm-vista-tabella" title="Vista tabella" className={`p-2 ${vista === "tabella" ? "bg-primary text-white" : "bg-white text-muted-foreground hover:bg-secondary/60"}`}><List size={16} /></button>
            <button onClick={() => setVista("griglia")} data-testid="amm-vista-griglia" title="Vista a card" className={`p-2 ${vista === "griglia" ? "bg-primary text-white" : "bg-white text-muted-foreground hover:bg-secondary/60"}`}><LayoutGrid size={16} /></button>
          </div>
        </div>
      </div>

      {vista === "tabella" ? (
        <div className="gr-card p-0 overflow-x-auto" data-testid="amm-months-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b bg-secondary/30">
                <th className="py-2.5 px-4">Mese</th>
                <th className="py-2.5 px-4 text-center">Cespiti</th>
                <th className="py-2.5 px-4 text-right">Quota del mese</th>
                <th className="py-2.5 px-4 text-right">Di cui deducibile</th>
                <th className="py-2.5 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {MESI.map((label, m) => {
                const mk = mkeyOf(m); const t = totMese(mk);
                return (
                  <tr key={m} onClick={() => setDetail(mk)} data-testid={`amm-month-row-${m + 1}`}
                    className="border-b last:border-0 cursor-pointer hover:bg-secondary/40 transition-colors">
                    <td className="py-2.5 px-4 font-semibold">{label} {anno}</td>
                    <td className="py-2.5 px-4 text-center tabnum text-muted-foreground">{t.n || "—"}</td>
                    <td className={`py-2.5 px-4 text-right tabnum font-bold ${t.tot > 0 ? "text-amber-700" : "text-muted-foreground"}`}>{fmtEur(t.tot)}</td>
                    <td className="py-2.5 px-4 text-right tabnum text-primary">{fmtEur(t.ded)}</td>
                    <td className="py-2.5 px-4 text-right"><ChevronRight size={16} className="text-muted-foreground inline" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="amm-months-grid">
          {MESI.map((label, m) => {
            const mk = mkeyOf(m); const t = totMese(mk);
            return (
              <button key={m} onClick={() => setDetail(mk)} data-testid={`amm-month-${m + 1}`}
                className="gr-card p-4 text-left hover:border-primary transition-colors">
                <div className="flex items-center justify-between">
                  <span className="font-head font-bold">{label}</span>
                  <CalendarRange size={16} className="text-muted-foreground" />
                </div>
                <div className={`text-xl font-head font-bold tabnum mt-2 ${t.tot > 0 ? "text-amber-700" : "text-muted-foreground"}`}>{fmtEur(t.tot)}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{t.n > 0 ? `${t.n} cespiti · ded. ${fmtEur(t.ded)}` : "Nessun ammortamento"}</div>
              </button>
            );
          })}
        </div>
      )}
      {AddDialog}
    </div>
  );
}
